import React, { createContext, useContext, type ReactNode } from "react";
import { useWebWorker } from "../hooks/useWebWorker";
import type { UseWebWorkerReturn } from "../hooks/useWebWorker";

const WebWorkerContext = createContext<UseWebWorkerReturn | null>(null);

/**
 * Web Worker를 단일 인스턴스로 공유하는 Provider.
 * useUnityMessageHandler와 useViewRUpdate가 동일 Worker를 사용하여 메모리 절감.
 * @see docs/MEMORY_ISSUE_ANALYSIS_AND_JSON_FIX.md
 */
export function WebWorkerProvider({
  children,
  workerPath = "/workers/jsonWorker.js",
}: {
  children: ReactNode;
  workerPath?: string;
}): React.ReactElement {
  const worker = useWebWorker(workerPath);

  return (
    <WebWorkerContext.Provider value={worker}>
      {children}
    </WebWorkerContext.Provider>
  );
}

/**
 * WebWorkerProvider 내부에서만 사용 가능.
 * 공유된 JSON Worker 인스턴스를 반환.
 */
export function useSharedWebWorker(): UseWebWorkerReturn {
  const ctx = useContext(WebWorkerContext);
  if (ctx == null) {
    throw new Error(
      "useSharedWebWorker must be used within WebWorkerProvider. " +
        "Wrap EDITOR (or parent) with <WebWorkerProvider>."
    );
  }
  return ctx;
}
